import { BasicState } from "@app/geography";
import { BenchmarkingDashboardDetails } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardDetails";
import { BenchmarkingProviderFlyoutData } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingProviderFlyoutData";
import { PrimaryFilterOptionUpdates } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/PrimaryFilterOptionUpdates";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { BenchmarkingFilterData } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingFilterData";
import { CompetitiveInsightsManageAddModalOptionsModel } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsManageAddModalOptionsModel";
import { SupersetProjectConfigurationRequestModel } from "@app/overviews/network-benchmarking-dashboard/manage/shared/SupersetProjectConfigurationRequestModel";
import { QHPContractPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPContractPrimaryFiltersPayload";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import { QHPNetworkFilterData } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetworkFilterData";
import { QHPPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPPrimaryFiltersPayload";
import { EffectivenessAndEfficiencyData, HospitalAndPcpCountData, MarketPenetrationData, ProvidersByNetworkAndVolumeData, ProvidersByNetworkData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { AffiliationsData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/AffiliationsData";
import { ProviderOverlapData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/ProviderOverlapData";
import { authenticatedDownloadAsync } from "@app/shared/AuthenticatedDownload";
import ServiceBase from "@app/shared/serviceBase";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingLineOfBusinessEnum";

/** handles communication across the wire when interacting with the network benchmarking dashboard */
export default class BenchmarkingQHPService extends ServiceBase {
  private benchmarkingQHPEndpoint = `${this.apiEndpoint}/benchmarkingmarketplace`;
  public readonly lineOfBusiness = BenchmarkingLineOfBusinessEnum.Marketplace;

  public async getBenchmarkingPrimaryFilterData(signal?: AbortSignal): Promise<BenchmarkingFilterData> {
    return await this.makeGet<BenchmarkingFilterData>(`primary-filters`, null, this.benchmarkingQHPEndpoint, false, undefined, signal);
  }

  public async getBenchmarkingNetworkData(primaryFilters: QHPPrimaryFiltersPayload): Promise<QHPNetworkFilterData> {
    if (!primaryFilters) {
      return null;
    }

    return await this.makePost<QHPNetworkFilterData>(`get-networks`, primaryFilters, this.benchmarkingQHPEndpoint);
  }

  public async getContractDetails(primaryFilters: QHPContractPrimaryFiltersPayload, signal?: AbortSignal): Promise<QHPNetwork> {
    if (!primaryFilters) {
      return null;
    }

    return await this.makePost<QHPNetwork>(`contract`, primaryFilters, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getHospitalAndPcpCountData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<HospitalAndPcpCountData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<HospitalAndPcpCountData[]>(`hospital-and-pcp-count`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProvidersByNetworkData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<ProvidersByNetworkData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<ProvidersByNetworkData[]>(`providers-by-network`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getMarketPenetrationData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<MarketPenetrationData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<MarketPenetrationData[]>(`market-penetration`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProvidersByNetworkAndVolumeData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<ProvidersByNetworkAndVolumeData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<ProvidersByNetworkAndVolumeData[]>(`providers-by-network-and-volume`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getEffectivenessAndEfficiencyData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<EffectivenessAndEfficiencyData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<EffectivenessAndEfficiencyData[]>(`effectiveness-and-efficiency`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProviderOverlapData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<ProviderOverlapData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<ProviderOverlapData>(`provider-overlap`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getAffiliationsData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<AffiliationsData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<AffiliationsData[]>(`affiliations`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getDashboardDetailsData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<BenchmarkingDashboardDetails[]> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingDashboardDetails[]>(`dashboard-details`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProviderFlyoutData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<BenchmarkingProviderFlyoutData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingProviderFlyoutData>(`provider-details`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getQhpStates(): Promise<BasicState[]> {
    return await this.makeGet<BasicState[]>(`get-qhp-states`, null, this.benchmarkingQHPEndpoint);
  }

  public async getAddConfigurationOptions(clientId: number, signal?: AbortSignal): Promise<CompetitiveInsightsManageAddModalOptionsModel> {
    return await this.makeGet<CompetitiveInsightsManageAddModalOptionsModel>(`get-add-configuration-options?clientId=${clientId}`, null, this.benchmarkingQHPEndpoint, false, undefined, signal);
  }

  public async addSupersetProjectConfiguration(model: SupersetProjectConfigurationRequestModel): Promise<void> {
    return await this.makePost<void>(`add-superset-project-config`, model, this.benchmarkingQHPEndpoint);
  }

  public async editSupersetProjectConfiguration(model: SupersetProjectConfigurationRequestModel): Promise<void> {
    return await this.makePut<void>(`edit-superset-project-config`, model, this.benchmarkingQHPEndpoint);
  }

  public async getPrimaryFilterOptionUpdates(stateId: number, program: string | null, signal?: AbortSignal): Promise<PrimaryFilterOptionUpdates> {
    return await this.makeGet<PrimaryFilterOptionUpdates>(
      `get-primary-filter-option-updates?stateId=${stateId}&program=${program}`,
      null,
      this.benchmarkingQHPEndpoint,
      false,
      undefined,
      signal
    );
  }

  public async getDataCoverageCsv(fileName?: string): Promise<void> {
    if (!!fileName && fileName.split('.').pop().toLowerCase() !== `csv`) {
      fileName += `.csv`;
    }

    return await authenticatedDownloadAsync(`${this.benchmarkingQHPEndpoint}/data-coverage/report/csv`, null, fileName, new URLSearchParams());
  }
}
